import { useState, useCallback, useRef } from "react";

// ─── SYSTEM PROMPT ────────────────────────────────────────────────────────────
const SYSTEM_PROMPT = `You are a contract analysis expert. Multiple files may be provided — treat them as a single contract package (main agreement + invoices + addenda + DocuSign envelopes). Extract ALL key data and return ONLY valid JSON with this exact structure:

{
  "contractType": "Loan | Lease | Rental | Unknown",
  "contractNumber": "",
  "contractStartDate": "",
  "term": "",
  "paymentStartDate": "",
  "paymentAmount": "",
  "paymentFrequency": "",
  "lender": { "name": "", "address": "", "phone": "", "email": "", "contact": "" },
  "borrower": { "name": "", "address": "", "phone": "", "email": "", "contact": "" },
  "vendor": { "name": "", "address": "", "phone": "", "email": "", "contact": "" },
  "otherParties": [{ "name": "", "role": "", "address": "", "phone": "", "email": "", "contact": "" }],
  "additionalParties": [{ "name": "", "role": "", "address": "", "phone": "", "email": "", "contact": "" }],
  "financials": { "equipmentCost": "", "downPayment": "", "amountFinanced": "", "monthlyPayment": "", "interestRate": "", "fees": "" },
  "collateral": [{ "description": "", "unitNumber": "", "value": "" }],
  "addenda": [{ "title": "", "date": "", "description": "" }],
  "signatories": [{ "name": "", "role": "", "signedDate": "" }],
  "keyTerms": { "governingLaw": "", "venue": "", "prepaymentPenalty": "", "lateFee": "", "insuranceRequired": "" },
  "notes": ""
}

Rules: Use "" for missing strings, [] for missing arrays. otherParties = guarantors/co-signers/co-obligors. additionalParties = agents/brokers/trustees/assignees/platform providers. paymentStartDate = first payment due date. Parse ALL addenda, amendments, corrections, riders. Extract ALL signatories.`;

// ─── API CALL ─────────────────────────────────────────────────────────────────
async function extractFromFiles(fileDataList) {
  const content = [];
  for (const { base64, mediaType } of fileDataList) {
    content.push({ type: "document", source: { type: "base64", media_type: mediaType, data: base64 } });
  }
  content.push({ type: "text", text: "Extract all contract data from these documents as a single contract package. Return JSON only." });

  const response = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    // headers: { "Content-Type": "application/json" },
    headers: {
    "Content-Type": "application/json",
    "x-api-key": "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",   // 👈 ADD THIS
    "anthropic-version": "2023-06-01"        // 👈 REQUIRED
  },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: 4000,
      system: SYSTEM_PROMPT,
      messages: [{ role: "user", content }]
    })
  });
  const data = await response.json();
  const text = data.content?.map(b => b.text || "").join("") || "";
  return JSON.parse(text.replace(/```json|```/g, "").trim());
}

async function fileToBase64(file) {
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result.split(",")[1]);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });
}

function getMediaType(file) {
  if (file.type === "application/pdf") return "application/pdf";
  if (file.type.startsWith("image/")) return file.type;
  return "application/pdf";
}

// ─── EMPTY CONTRACT TEMPLATE ──────────────────────────────────────────────────
const emptyContract = () => ({
  contractType: "Unknown", contractNumber: "", contractStartDate: "", term: "",
  paymentStartDate: "", paymentAmount: "", paymentFrequency: "",
  lender: { name: "", address: "", phone: "", email: "", contact: "" },
  borrower: { name: "", address: "", phone: "", email: "", contact: "" },
  vendor: { name: "", address: "", phone: "", email: "", contact: "" },
  otherParties: [], additionalParties: [],
  financials: { equipmentCost: "", downPayment: "", amountFinanced: "", monthlyPayment: "", interestRate: "", fees: "" },
  collateral: [], addenda: [], signatories: [],
  keyTerms: { governingLaw: "", venue: "", prepaymentPenalty: "", lateFee: "", insuranceRequired: "" },
  notes: ""
});

// ─── STYLES ───────────────────────────────────────────────────────────────────
const S = {
  input: { width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "7px 10px", fontSize: 13, color: "#1f2937", background: "white", outline: "none", boxSizing: "border-box", fontFamily: "Georgia, serif" },
  inputFocus: { borderColor: "#e8c547", boxShadow: "0 0 0 2px rgba(232,197,71,0.2)" },
  label: { fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4, display: "block" },
  missingBadge: { fontSize: 10, background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a", borderRadius: 4, padding: "1px 6px", marginLeft: 6 },
  editedBadge: { fontSize: 10, background: "#d1fae5", color: "#065f46", border: "1px solid #a7f3d0", borderRadius: 4, padding: "1px 6px", marginLeft: 6 },
};

// ─── EDITABLE FIELD ───────────────────────────────────────────────────────────
function EditableField({ label, value, originalValue, onChange, placeholder, type = "text" }) {
  const [focused, setFocused] = useState(false);
  const isEmpty = !value || value.trim() === "";
  const wasEdited = value !== originalValue && originalValue !== undefined;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
        <label style={S.label}>{label}</label>
        {isEmpty && <span style={S.missingBadge}>Missing</span>}
        {!isEmpty && wasEdited && <span style={S.editedBadge}>Edited</span>}
      </div>
      <input
        type={type}
        value={value || ""}
        placeholder={placeholder || `Enter ${label.toLowerCase()}…`}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{ ...S.input, ...(focused ? S.inputFocus : {}), background: isEmpty ? "#fffbeb" : "white" }}
      />
    </div>
  );
}

// ─── EDITABLE SELECT ──────────────────────────────────────────────────────────
function EditableSelect({ label, value, originalValue, onChange, options }) {
  const wasEdited = value !== originalValue && originalValue !== undefined;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
        <label style={S.label}>{label}</label>
        {wasEdited && <span style={S.editedBadge}>Edited</span>}
      </div>
      <select value={value || ""} onChange={e => onChange(e.target.value)}
        style={{ ...S.input, cursor: "pointer" }}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

// ─── SECTION WRAPPER ─────────────────────────────────────────────────────────
function Section({ title, icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background: "white", borderRadius: 16, border: "1px solid #e5e7eb", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 16, overflow: "hidden" }}>
      <button onClick={() => setOpen(!open)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "11px 20px", background: "#f9fafb", borderBottom: open ? "1px solid #e5e7eb" : "none", border: "none", cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 15 }}>{icon}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em" }}>{title}</span>
        </div>
        <span style={{ color: "#9ca3af", fontSize: 12 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && <div style={{ padding: 20 }}>{children}</div>}
    </div>
  );
}

// ─── PARTY EDITOR ─────────────────────────────────────────────────────────────
function PartyEditor({ title, borderColor, value, original, onChange }) {
  const up = (field, val) => onChange({ ...value, [field]: val });
  const orig = original || {};
  return (
    <div style={{ borderLeft: `4px solid ${borderColor}`, paddingLeft: 16 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>{title}</div>
      <EditableField label="Name" value={value.name} originalValue={orig.name} onChange={v => up("name", v)} />
      <EditableField label="Contact" value={value.contact} originalValue={orig.contact} onChange={v => up("contact", v)} />
      <EditableField label="Address" value={value.address} originalValue={orig.address} onChange={v => up("address", v)} />
      <EditableField label="Phone" value={value.phone} originalValue={orig.phone} onChange={v => up("phone", v)} />
      <EditableField label="Email" value={value.email} originalValue={orig.email} onChange={v => up("email", v)} />
    </div>
  );
}

// ─── ARRAY ITEM EDITOR ────────────────────────────────────────────────────────
function ArrayEditor({ items, fields, onChange, addLabel, emptyItem }) {
  const update = (idx, field, val) => {
    const next = items.map((it, i) => i === idx ? { ...it, [field]: val } : it);
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { ...emptyItem }]);
  return (
    <div>
      {items.map((item, idx) => (
        <div key={idx} style={{ background: "#f8f7f4", borderRadius: 10, padding: 14, marginBottom: 10, position: "relative" }}>
          <button onClick={() => remove(idx)} style={{ position: "absolute", top: 10, right: 10, background: "#fee2e2", border: "none", borderRadius: 6, padding: "2px 8px", fontSize: 11, color: "#dc2626", cursor: "pointer" }}>✕</button>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            {fields.map(f => (
              <EditableField key={f.key} label={f.label} value={item[f.key]} onChange={v => update(idx, f.key, v)} />
            ))}
          </div>
        </div>
      ))}
      <button onClick={add} style={{ background: "white", border: "1px dashed #d1d5db", borderRadius: 8, padding: "8px 16px", fontSize: 12, color: "#6b7280", cursor: "pointer", width: "100%" }}>
        ＋ {addLabel}
      </button>
    </div>
  );
}

// ─── BADGE ────────────────────────────────────────────────────────────────────
function Badge({ type }) {
  const styles = { Loan: { bg: "#dbeafe", color: "#1e40af", border: "#bfdbfe" }, Lease: { bg: "#ede9fe", color: "#6d28d9", border: "#ddd6fe" }, Rental: { bg: "#dcfce7", color: "#166534", border: "#bbf7d0" }, Unknown: { bg: "#f3f4f6", color: "#6b7280", border: "#e5e7eb" } };
  const s = styles[type] || styles.Unknown;
  return <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: 999, padding: "3px 12px", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>{type}</span>;
}

// ─── MISSING FIELDS COUNTER ───────────────────────────────────────────────────
function countMissing(data) {
  let count = 0;
  const check = (v) => { if (!v || v.trim() === "") count++; };
  ["contractType", "contractNumber", "contractStartDate", "term", "paymentStartDate", "paymentAmount"].forEach(k => check(data[k]));
  ["lender", "borrower", "vendor"].forEach(party => ["name", "address", "phone", "email"].forEach(f => check(data[party]?.[f])));
  return count;
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function CreateContractAgent() {
  const [step, setStep] = useState("upload");
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState("");
  const [progressIdx, setProgressIdx] = useState(0);
  const [data, setData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef();

  const progressSteps = ["Reading documents…", "Identifying contract type…", "Extracting parties…", "Parsing payment terms…", "Finding additional parties…", "Reviewing addenda…", "Extracting signatories…", "Finalizing…"];

  const addFiles = (newFiles) => {
    const valid = Array.from(newFiles).filter(f => f.type === "application/pdf" || f.type.startsWith("image/"));
    setFiles(prev => {
      const existing = new Set(prev.map(f => f.name));
      return [...prev, ...valid.filter(f => !existing.has(f.name))];
    });
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    addFiles(e.dataTransfer.files);
  }, []);

  const analyze = async () => {
    if (!files.length) return;
    setError(null); setStep("extracting");
    let i = 0; setProgressIdx(0); setProgress(progressSteps[0]);
    const interval = setInterval(() => { i = Math.min(i + 1, progressSteps.length - 1); setProgressIdx(i); setProgress(progressSteps[i]); }, 1500);
    try {
      const fileDataList = await Promise.all(files.map(async f => ({ base64: await fileToBase64(f), mediaType: getMediaType(f) })));
      const result = await extractFromFiles(fileDataList);
      clearInterval(interval);
      setData(result);
      setOriginalData(JSON.parse(JSON.stringify(result)));
      setStep("review");
    } catch (err) {
      clearInterval(interval);
      setError("Extraction failed. Please check your files and try again.");
      setStep("upload");
    }
  };

  const startManual = () => {
    const blank = emptyContract();
    setData(blank);
    setOriginalData(JSON.parse(JSON.stringify(blank)));
    setStep("review");
  };

  const reset = () => { setStep("upload"); setFiles([]); setData(null); setOriginalData(null); setError(null); setProgress(""); };

  const update = (path, value) => {
    setData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split(".");
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) obj = obj[parts[i]];
      obj[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const missingCount = data ? countMissing(data) : 0;

  return (
    <div style={{ fontFamily: "Georgia, serif", background: "#f8f7f4", minHeight: "100vh" }}>
      {/* ── HEADER ── */}
      <div style={{ background: "#1a1a2e", color: "white", padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ background: "#e8c547", borderRadius: 8, width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>📋</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Create Contract</div>
            <div style={{ color: "#9ca3af", fontSize: 11 }}>AI Contract Analysis Agent</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {step === "review" && missingCount > 0 && (
            <span style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a", borderRadius: 999, padding: "4px 12px", fontSize: 11, fontWeight: 700 }}>
              ⚠ {missingCount} missing field{missingCount !== 1 ? "s" : ""}
            </span>
          )}
          {step === "review" && (
            <button onClick={reset} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "white", borderRadius: 8, padding: "6px 16px", fontSize: 12, cursor: "pointer" }}>
              ＋ New Contract
            </button>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: "0 auto", padding: "32px 16px" }}>

        {/* ── UPLOAD ── */}
        {step === "upload" && (
          <div>
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <h1 style={{ color: "#1a1a2e", fontSize: 26, fontWeight: 700, marginBottom: 8 }}>Upload Contract Package</h1>
              <p style={{ color: "#6b7280", fontSize: 13 }}>Add all related files — main agreement, invoices, addenda, DocuSign envelopes</p>
            </div>

            {error && <div style={{ marginBottom: 16, padding: 14, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, color: "#b91c1c", fontSize: 13 }}>{error}</div>}

            {/* Drop Zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current.click()}
              style={{ border: `2px dashed ${dragging ? "#e8c547" : "#d1d5db"}`, background: dragging ? "#fffdf0" : "white", borderRadius: 20, cursor: "pointer", padding: "48px 32px", textAlign: "center", transition: "all 0.2s", marginBottom: 16 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📂</div>
              <div style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}>Drop files here</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>PDF, PNG, JPG — multiple files supported</div>
              <div style={{ background: "#1a1a2e", color: "white", borderRadius: 10, display: "inline-block", padding: "9px 22px", fontSize: 13, fontWeight: 600 }}>Browse Files</div>
              <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg" multiple style={{ display: "none" }} onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
            </div>

            {/* File List */}
            {files.length > 0 && (
              <div style={{ background: "white", borderRadius: 14, border: "1px solid #e5e7eb", marginBottom: 16, overflow: "hidden" }}>
                <div style={{ padding: "10px 16px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  {files.length} File{files.length !== 1 ? "s" : ""} Selected
                </div>
                {files.map((f, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: i < files.length - 1 ? "1px solid #f3f4f6" : "none" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 18 }}>{f.type === "application/pdf" ? "📄" : "🖼️"}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: "#1f2937" }}>{f.name}</div>
                        <div style={{ fontSize: 11, color: "#9ca3af" }}>{(f.size / 1024).toFixed(0)} KB</div>
                      </div>
                    </div>
                    <button onClick={() => removeFile(i)} style={{ background: "#fee2e2", border: "none", borderRadius: 6, padding: "3px 10px", fontSize: 11, color: "#dc2626", cursor: "pointer" }}>Remove</button>
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={analyze} disabled={!files.length}
                style={{ background: files.length ? "#1a1a2e" : "#d1d5db", color: "white", borderRadius: 12, padding: "12px 32px", fontWeight: 600, fontSize: 13, cursor: files.length ? "pointer" : "not-allowed", border: "none" }}>
                🤖 Analyze {files.length > 1 ? `${files.length} Files` : "Contract"}
              </button>
              <button onClick={startManual}
                style={{ background: "white", color: "#374151", borderRadius: 12, padding: "12px 24px", fontWeight: 600, fontSize: 13, cursor: "pointer", border: "1px solid #e5e7eb" }}>
                ✏️ Enter Manually
              </button>
            </div>
          </div>
        )}

        {/* ── EXTRACTING ── */}
        {step === "extracting" && (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div style={{ fontSize: 52, marginBottom: 20 }}>🤖</div>
            <h2 style={{ color: "#1a1a2e", fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Analyzing {files.length} File{files.length !== 1 ? "s" : ""}</h2>
            <p style={{ color: "#6b7280", fontSize: 13, marginBottom: 24 }}>{files.map(f => f.name).join(" · ")}</p>
            <div style={{ background: "white", borderRadius: 14, border: "1px solid #e5e7eb", display: "inline-block", padding: "14px 28px", marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 16, height: 16, border: "2px solid #1a1a2e", borderTopColor: "#e8c547", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <span style={{ fontSize: 13, color: "#4b5563", fontWeight: 500 }}>{progress}</span>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 5 }}>
              {progressSteps.map((_, i) => (
                <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: i <= progressIdx ? "#1a1a2e" : "#d1d5db", transition: "background 0.3s" }} />
              ))}
            </div>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}

        {/* ── REVIEW & EDIT ── */}
        {step === "review" && data && (
          <div>
            {/* Info banner */}
            <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12, padding: "10px 16px", marginBottom: 16, fontSize: 12, color: "#1e40af", display: "flex", alignItems: "center", gap: 8 }}>
              <span>✏️</span>
              <span>All fields are editable — click any field to correct or fill in missing information. <span style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a", borderRadius: 4, padding: "1px 5px", fontSize: 10 }}>Missing</span> fields are highlighted in yellow.</span>
            </div>

            {/* Summary Bar */}
            <div style={{ background: "#1a1a2e", borderRadius: 16, color: "white", padding: 20, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3 }}>Contract No.</div>
                    <input value={data.contractNumber || ""} onChange={e => update("contractNumber", e.target.value)} placeholder="Enter contract number…"
                      style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, padding: "5px 10px", color: "white", fontSize: 15, fontWeight: 700, width: 160, fontFamily: "Georgia, serif" }} />
                  </div>
                  <Badge type={data.contractType || "Unknown"} />
                </div>
                <select value={data.contractType || "Unknown"} onChange={e => update("contractType", e.target.value)}
                  style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, padding: "6px 10px", color: "white", fontSize: 12, fontFamily: "Georgia, serif", cursor: "pointer" }}>
                  {["Loan", "Lease", "Rental", "Unknown"].map(t => <option key={t} value={t} style={{ color: "#1f2937" }}>{t}</option>)}
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
                {[
                  ["Start Date", "contractStartDate", "e.g. 2025-06-21"],
                  ["Term", "term", "e.g. 48 months"],
                  ["Payment Start", "paymentStartDate", "e.g. 2025-08-01"],
                  ["Payment Amount", "paymentAmount", "e.g. $6,172.59"]
                ].map(([label, key, ph]) => (
                  <div key={key} style={{ background: "rgba(255,255,255,0.07)", borderRadius: 10, padding: 10 }}>
                    <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 5 }}>{label}</div>
                    <input value={data[key] || ""} onChange={e => update(key, e.target.value)} placeholder={ph}
                      style={{ background: "transparent", border: "none", borderBottom: "1px solid rgba(255,255,255,0.2)", color: "white", fontSize: 12, fontWeight: 600, width: "100%", outline: "none", padding: "2px 0", fontFamily: "Georgia, serif" }} />
                  </div>
                ))}
              </div>
            </div>

            {/* PRIMARY PARTIES */}
            <Section title="Primary Parties" icon="👥">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24 }}>
                <PartyEditor title="Lender" borderColor="#3b82f6" value={data.lender} original={originalData?.lender} onChange={v => update("lender", v)} />
                <PartyEditor title="Borrower" borderColor="#22c55e" value={data.borrower} original={originalData?.borrower} onChange={v => update("borrower", v)} />
                <PartyEditor title="Vendor / Supplier" borderColor="#f97316" value={data.vendor} original={originalData?.vendor} onChange={v => update("vendor", v)} />
              </div>
            </Section>

            {/* OTHER PARTIES */}
            <Section title="Other Parties (Guarantors, Co-signers)" icon="🤝" defaultOpen={data.otherParties?.length > 0}>
              <ArrayEditor
                items={data.otherParties || []}
                onChange={v => update("otherParties", v)}
                addLabel="Add Guarantor / Co-signer"
                emptyItem={{ name: "", role: "Guarantor", address: "", phone: "", email: "", contact: "" }}
                fields={[
                  { key: "name", label: "Name" }, { key: "role", label: "Role" },
                  { key: "contact", label: "Contact" }, { key: "email", label: "Email" },
                  { key: "phone", label: "Phone" }, { key: "address", label: "Address" }
                ]}
              />
            </Section>

            {/* ADDITIONAL PARTIES */}
            <Section title="Additional Parties" icon="➕" defaultOpen={data.additionalParties?.length > 0}>
              <ArrayEditor
                items={data.additionalParties || []}
                onChange={v => update("additionalParties", v)}
                addLabel="Add Additional Party"
                emptyItem={{ name: "", role: "", address: "", phone: "", email: "", contact: "" }}
                fields={[
                  { key: "name", label: "Name" }, { key: "role", label: "Role" },
                  { key: "contact", label: "Contact" }, { key: "email", label: "Email" },
                  { key: "phone", label: "Phone" }, { key: "address", label: "Address" }
                ]}
              />
            </Section>

            {/* PAYMENT TERMS */}
            <Section title="Payment Terms" icon="💳">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 16px" }}>
                <EditableField label="Payment Amount" value={data.paymentAmount} originalValue={originalData?.paymentAmount} onChange={v => update("paymentAmount", v)} />
                <EditableField label="Payment Start Date" value={data.paymentStartDate} originalValue={originalData?.paymentStartDate} onChange={v => update("paymentStartDate", v)} />
                <EditableField label="Frequency" value={data.paymentFrequency} originalValue={originalData?.paymentFrequency} onChange={v => update("paymentFrequency", v)} />
                <EditableField label="Amount Financed" value={data.financials?.amountFinanced} originalValue={originalData?.financials?.amountFinanced} onChange={v => update("financials.amountFinanced", v)} />
                <EditableField label="Interest Rate" value={data.financials?.interestRate} originalValue={originalData?.financials?.interestRate} onChange={v => update("financials.interestRate", v)} />
                <EditableField label="Down Payment" value={data.financials?.downPayment} originalValue={originalData?.financials?.downPayment} onChange={v => update("financials.downPayment", v)} />
                <EditableField label="Equipment Cost" value={data.financials?.equipmentCost} originalValue={originalData?.financials?.equipmentCost} onChange={v => update("financials.equipmentCost", v)} />
                <EditableField label="Fees" value={data.financials?.fees} originalValue={originalData?.financials?.fees} onChange={v => update("financials.fees", v)} />
              </div>
            </Section>

            {/* COLLATERAL */}
            <Section title="Collateral / Equipment" icon="🚜" defaultOpen={data.collateral?.length > 0}>
              <ArrayEditor
                items={data.collateral || []}
                onChange={v => update("collateral", v)}
                addLabel="Add Equipment / Collateral"
                emptyItem={{ description: "", unitNumber: "", value: "" }}
                fields={[
                  { key: "description", label: "Description" }, { key: "unitNumber", label: "Unit #" },
                  { key: "value", label: "Value" }
                ]}
              />
            </Section>

            {/* ADDENDA */}
            <Section title="Addenda & Amendments" icon="📎" defaultOpen={data.addenda?.length > 0}>
              <ArrayEditor
                items={data.addenda || []}
                onChange={v => update("addenda", v)}
                addLabel="Add Addendum / Amendment"
                emptyItem={{ title: "", date: "", description: "" }}
                fields={[
                  { key: "title", label: "Title" }, { key: "date", label: "Date" },
                  { key: "description", label: "Description" }
                ]}
              />
            </Section>

            {/* SIGNATORIES */}
            <Section title="Signatories" icon="✍️" defaultOpen={data.signatories?.length > 0}>
              <ArrayEditor
                items={data.signatories || []}
                onChange={v => update("signatories", v)}
                addLabel="Add Signatory"
                emptyItem={{ name: "", role: "", signedDate: "" }}
                fields={[
                  { key: "name", label: "Name" }, { key: "role", label: "Role" },
                  { key: "signedDate", label: "Signed Date" }
                ]}
              />
            </Section>

            {/* KEY LEGAL TERMS */}
            <Section title="Key Legal Terms" icon="⚖️" defaultOpen={false}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <EditableField label="Governing Law" value={data.keyTerms?.governingLaw} originalValue={originalData?.keyTerms?.governingLaw} onChange={v => update("keyTerms.governingLaw", v)} />
                <EditableField label="Venue" value={data.keyTerms?.venue} originalValue={originalData?.keyTerms?.venue} onChange={v => update("keyTerms.venue", v)} />
                <EditableField label="Prepayment Penalty" value={data.keyTerms?.prepaymentPenalty} originalValue={originalData?.keyTerms?.prepaymentPenalty} onChange={v => update("keyTerms.prepaymentPenalty", v)} />
                <EditableField label="Late Fee" value={data.keyTerms?.lateFee} originalValue={originalData?.keyTerms?.lateFee} onChange={v => update("keyTerms.lateFee", v)} />
                <EditableField label="Insurance Required" value={data.keyTerms?.insuranceRequired} originalValue={originalData?.keyTerms?.insuranceRequired} onChange={v => update("keyTerms.insuranceRequired", v)} />
              </div>
            </Section>

            {/* NOTES */}
            <Section title="Additional Notes" icon="📝" defaultOpen={false}>
              <textarea value={data.notes || ""} onChange={e => update("notes", e.target.value)} placeholder="Add any additional notes or observations…"
                style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 13, color: "#1f2937", fontFamily: "Georgia, serif", minHeight: 80, resize: "vertical", boxSizing: "border-box", outline: "none" }} />
            </Section>

            {/* ACTIONS */}
            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 8, flexWrap: "wrap" }}>
              <button onClick={() => { const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `contract-${data.contractNumber || "export"}.json`; a.click(); }}
                style={{ background: "white", color: "#374151", border: "1px solid #e5e7eb", borderRadius: 12, padding: "11px 24px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                ⬇ Export JSON
              </button>
              <button onClick={reset} style={{ background: "#1a1a2e", color: "white", borderRadius: 12, padding: "11px 28px", fontWeight: 600, fontSize: 13, cursor: "pointer", border: "none" }}>
                ＋ New Contract
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
